'use client';

import React, { useRef, useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface Testimonial {
  id: string;
  client_name: string;
  company: string;
  content: string;
  rating: number;
  avatar_url?: string;
}

export default function TestimonialsSection() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const supabase = createClient();

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setError(null);
        const { data, error } = await supabase
          .from('testimonials')
          .select('*')
          .eq('is_active', true)
          .order('order_position', { ascending: true });

        if (error) {
          console.error('Error fetching testimonials:', error.message);
          setError(error.message);
        } else {
          setTestimonials(data || []);
        }
      } catch (err) {
        console.error('Error:', err);
        setError('Failed to load testimonials');
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, [supabase]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      const newScrollLeft =
        scrollContainerRef.current.scrollLeft +
        (direction === 'right' ? scrollAmount : -scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  // Show loading state
  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Opinie Klientów
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Zobacz, co mówią nasi klienci o migracjach i wdrożeniach systemów
              BaseLinker i Apilo
            </p>
          </div>
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-DEFAULT"></div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state with helpful message
  if (error && testimonials.length === 0) {
    return (
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Opinie Klientów
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Zobacz, co mówią nasi klienci o migracjach i wdrożeniach systemów
              BaseLinker i Apilo
            </p>
          </div>
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">Ładowanie opinii...</p>
            <p className="text-sm text-gray-400">Jeśli problem się powtarza, odśwież stronę</p>
          </div>
        </div>
      </section>
    );
  }

  // Don't render section if no testimonials
  if (testimonials.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Opinie Klientów
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Zobacz, co mówią nasi klienci o migracjach i wdrożeniach systemów
            BaseLinker i Apilo
          </p>
        </div>

        {/* Testimonials Slider */}
        <div className="relative">
          {/* Left Arrow */}
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-colors hidden md:block"
            aria-label="Poprzednia opinia"
          >
            <Icon
              name="ChevronLeftIcon"
              className="w-6 h-6 text-gray-700"
            />
          </button>

          {/* Right Arrow */}
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-colors hidden md:block"
            aria-label="Następna opinia"
          >
            <Icon
              name="ChevronRightIcon"
              className="w-6 h-6 text-gray-700"
            />
          </button>

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4 px-2 md:px-12"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {testimonials?.map((testimonial) => (
              <div
                key={testimonial.id}
                className="flex-shrink-0 w-[350px] bg-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <Icon key={i} name="StarIcon" size={20} className="text-yellow-400" variant="solid" />
                  ))}
                </div>
                <p className="text-base text-navy font-500 mb-6 leading-relaxed">
                  {testimonial.content}
                </p>
                <div className="flex items-center gap-4">
                  {testimonial.avatar_url && (
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-DEFAULT font-700 text-lg">
                        {testimonial.client_name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-700 text-navy">
                      {testimonial.client_name}
                    </p>
                    <p className="text-xs text-muted font-500">
                      {testimonial.company}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Scroll Hint */}
        <div className="text-center mt-6 md:hidden">
          <p className="text-sm text-gray-500">
            <Icon
              name="ArrowRightIcon"
              className="w-4 h-4 inline-block mr-1"
            />
            Przesuń, aby zobaczyć więcej opinii
          </p>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}